import logger from '../utils/logger';
import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import API_URL from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Users, Search, ChevronRight, Mail, Briefcase } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const initials = (name) => (name || 'NA').split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();

// Flatten tree DFS into [{node, depth, parentId}]
const flattenTree = (roots) => {
    const flat = [];
    const stack = roots.slice().reverse().map(r => ({ node: r, depth: 0, parentId: null }));
    while (stack.length) {
        const item = stack.pop();
        flat.push(item);
        const kids = item.node.children || [];
        for (let i = kids.length - 1; i >= 0; i--) {
            stack.push({ node: kids[i], depth: item.depth + 1, parentId: item.node._id });
        }
    }
    return flat;
};

const OrgChart = () => {
    const { canManage } = useAuth();
    const [data, setData] = useState({ roots: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [collapsed, setCollapsed] = useState({});

    const fetchTree = useCallback(async () => {
        try {
            const { data: t } = await axios.get(`${API_URL}/api/employees/org-chart?tree=true`, { withCredentials: true });
            setData(t);
        } catch (e) {
            logger.error('org-chart', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchTree(); }, [fetchTree]);

    const toggle = (id) => setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));

    // Compute visible flat list: skip subtrees whose parent is collapsed; apply search
    const visible = useMemo(() => {
        const all = flattenTree(data.roots || []);
        const q = query.trim().toLowerCase();
        // Hide all descendants of collapsed nodes
        const hidden = new Set();
        all.forEach(({ node, parentId }) => {
            if (parentId && (hidden.has(parentId) || collapsed[parentId])) {
                hidden.add(node._id);
            }
        });
        return all.filter(({ node }) => !hidden.has(node._id))
            .map(item => ({
                ...item,
                matched: !q || (item.node.name || '').toLowerCase().includes(q) || (item.node.designation || '').toLowerCase().includes(q),
            }));
    }, [data.roots, collapsed, query]);

    const childCount = (node) => (node.children?.length || 0);

    return (
        <div className="space-y-6 max-w-5xl mx-auto" data-testid="org-chart-page">
            <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#2563eb]">Reporting Hierarchy</p>
                <h1 className="text-3xl sm:text-4xl font-black text-slate-900 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>Organization Chart</h1>
                <p className="text-sm text-slate-500 mt-2">Visualize the reporting tree across the company. {data.total} active employees.</p>
            </div>

            <Card className="rounded-xl border-slate-200 shadow-sm">
                <CardHeader className="border-b border-slate-200 py-3 flex-row items-center gap-3">
                    <Users className="h-4 w-4 text-[#2563eb]" />
                    <CardTitle className="text-sm font-bold flex-1" style={{ fontFamily: 'Chivo, sans-serif' }}>Hierarchy Tree</CardTitle>
                    <div className="relative w-72">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search by name or designation..."
                            className="pl-9 rounded-lg bg-slate-50 border-slate-200 text-sm"
                            data-testid="org-search-input"
                        />
                    </div>
                </CardHeader>
                <CardContent className="p-5">
                    {loading ? (
                        <div className="text-center py-12 text-slate-400 text-sm">Loading hierarchy…</div>
                    ) : data.roots.length === 0 ? (
                        <div className="text-center py-12 text-slate-400 text-sm">No employees yet.{canManage ? <a className="text-[#2563eb] font-semibold ml-1" href="/employees">Add employees</a> : ''}</div>
                    ) : (
                        <div className="space-y-2">
                            {visible.map(({ node, depth, matched }) => {
                                const kids = childCount(node);
                                const isCollapsed = collapsed[node._id];
                                return (
                                    <div
                                        key={node._id}
                                        className={`flex items-center gap-3 rounded-xl border ${matched ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50/50 opacity-60'} hover:border-[#2563eb]/40 hover:shadow-sm transition-all p-3 ${kids > 0 ? 'cursor-pointer' : ''}`}
                                        style={{ marginLeft: `${depth * 28}px` }}
                                        onClick={() => kids > 0 && toggle(node._id)}
                                        data-testid={`org-node-${node._id}`}
                                    >
                                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#2563eb] to-emerald-500 text-white font-bold flex items-center justify-center text-sm shrink-0">
                                            {initials(node.name)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <p className="font-bold text-sm text-slate-900 truncate" style={{ fontFamily: 'Chivo, sans-serif' }}>{node.name}</p>
                                                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">{(node.role || '').replace('_', ' ')}</span>
                                            </div>
                                            <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                                                {node.designation && <span className="flex items-center gap-1"><Briefcase className="h-3 w-3" />{node.designation}</span>}
                                                {node.employee_id && <span className="font-mono">{node.employee_id}</span>}
                                            </div>
                                        </div>
                                        {kids > 0 && (
                                            <div className="flex items-center gap-2 text-xs text-slate-500">
                                                <span className="font-bold tabular-nums">{kids}</span>
                                                <ChevronRight className={`h-4 w-4 transition-transform ${isCollapsed ? '' : 'rotate-90'}`} />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            {canManage && (
                <p className="text-xs text-slate-500 text-center">
                    💡 Set the <strong>Reporting Manager</strong> field on each employee record to build the tree. <Mail className="h-3 w-3 inline" /> When file status changes, the assigned RM and the customer get automatic email + SMS updates.
                </p>
            )}
        </div>
    );
};

export default OrgChart;
